import { useEffect, useState } from "react";
import { VEHICLES_URL } from "../../api/UserUrls";
import { post } from "../../services/smartApiService";
import ExcelJS from 'exceljs';
import { SmartAlert } from "soft_digi";

interface Props {
  year?: string;
  vehicle_type: any;
}

let months = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const HubCapacity: React.FC<Props> = ({ year, vehicle_type }) => {
  const [numberArray, setNumberArray] = useState<any[]>([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [data, setData] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");

  const loadData = () => {
    let _data = {
      year: year,
      vehicle_type: vehicle_type
    };
    let URL = VEHICLES_URL.GET_ALL_HUB_CAPACITY;
    const subscription = post(URL, _data).subscribe((response) => {
      setData(response.data.data);
      setNumberArray(response.data.dates);
    });
    return () => {
      subscription.unsubscribe();
    };
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const filteredData = data.filter(
    (item) =>
      item.hub_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.office_city.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getDayobj = (date: string, subData: any[]) => {
    if (subData && subData.length > 0) {
      let _date_obj: any = subData.find((obj) => obj.month == date);
      return _date_obj;
    }
    return 0;
  };

  const exportExcel = async () => {
    try {
      const sheetTitle = "Hub Wise Vehicle Report";
      const fileName = `Hub_Vehicle_Report_${new Date()
        .toISOString()
        .slice(0, 10)}.xlsx`;

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Sheet1");

      // Add title
      worksheet.mergeCells("A1:Z1");
      const titleCell = worksheet.getCell("A1");
      titleCell.value = sheetTitle;
      titleCell.font = { bold: true, size: 14 };
      titleCell.alignment = { horizontal: "center" };
      titleCell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFFF00" },
      };

      // Add headers
      const headers = ["Hub Name", "Location", "Capacity", ...numberArray];
      const headerRow = worksheet.addRow(headers);

      headerRow.eachCell((cell) => {
        cell.font = { bold: true };
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "D3D3D3" },
        };
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
      });

      // Add data rows
      filteredData.forEach((hub) => {
        const rowData = [
          hub.hub_name,
          hub.office_city,
          hub.hub_capacity,
          ...numberArray.map((item) => {
            const dayObj = getDayobj(item, hub.bill_data);
            return dayObj?._average || 0;
          }),
        ];
        
        const row = worksheet.addRow(rowData);
        
        // Apply color formatting based on capacity
        row.eachCell((cell, colNumber) => {
          if (colNumber > 3) { // Skip first 3 columns (name, location, capacity)
            const dayObj = getDayobj(numberArray[colNumber - 4], hub.bill_data);
            if (dayObj && dayObj._average < hub.hub_capacity) {
              cell.font = { color: { argb: "FFFF0000" } }; // Red for below capacity
            } else {
              cell.font = { color: { argb: "FF00FF00" } }; // Green for at/above capacity
            }
          }
          cell.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          };
        });
      });

      // Auto-size columns
      worksheet.columns.forEach((column) => {
        if (!column) return;

        let maxLength = 0;
        column.eachCell?.({ includeEmpty: true }, (cell) => {
          const columnLength = cell.value ? cell.value.toString().length : 0;
          if (columnLength > maxLength) {
            maxLength = columnLength;
          }
        });

        if (column) {
          column.width = Math.min(Math.max(maxLength + 2, 10), 50);
        }
      });

      worksheet.views = [{ state: "frozen", ySplit: 1 }];

      // Generate and download the file
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      return true;
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      SmartAlert.show({
        title: "Export Failed",
        content: "An error occurred while exporting hub vehicle data.",
        type: "danger",
      });
      return false;
    }
  };

  useEffect(() => {
    loadData();
  }, [year]);

  return (
    <div className="p-2 card smart-efl-table">
      <div className="columns is-multiline">
        <div className="column is-6 ">
          <div className="is-flex is-justify-content-space-between is-align-items-center">
            <h2 className="mt-1 is-size-5 site-title has-text-weight-bold">
              Hub Wise Vehicle Report
            </h2>
          </div>
        </div>
        <div className="column is-6">
          <div className="is-flex ">
            <div className="search-box sd-efl-input">
              <input
                className="input"
                type="text"
                placeholder="Search"
                value={searchTerm}
                onChange={handleSearch}
              />
            </div>
            <button
              className="button is-small is-success is-light ml-3"
              onClick={exportExcel}
            >
              <span className="icon is-small"><i className="fa fa-file-excel-o"></i></span>
              <span>Export Excel</span>
            </button>
          </div>
        </div>
        <div className="column is-12">
          <div className="scrollable-table">
            <table className="table is-bordered is-fullwidth smart-report-table smart-efl-table">
              <thead>
                <tr>
                  <th>Hub Name</th>
                  <th>Location</th>
                  <th>Capacity</th>
                  {numberArray.map((item: any, index) => (
                    <th key={index}>{item}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredData &&
                  filteredData.map((hub) => (
                    <tr key={hub.hub_name}>
                      <td>
                        <div className="is-flex ">
                          <p>{hub.hub_name}</p>
                        </div>
                      </td>
                      <td>
                        <label className="is-size-7 has-text-info">
                          {hub.office_city}
                        </label>
                      </td>
                      <td>
                        <p>{hub.hub_capacity}</p>
                      </td>
                      {numberArray.map((item: any, index) => {
                        let _count = getDayobj(item, hub.bill_data);
                        return (
                          <td key={index} className="has-text-centered">
                            <span className={_count?._average < hub.hub_capacity ? "has-text-danger" : "has-text-success" }>                            
                              {_count?._average || 0}                             
                            </span>
                          </td>
                        ) 
                      })}
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HubCapacity;